/**
 * Codex PR #784 round-12 P2 (`scene/FrameLoops.tsx:1097`) — the userData
 * CONTRACT for an ATTRIBUTED viewport point-proxy cloud.
 *
 * # The gap this closes
 *
 * Round 11 gave a paper-space VIEWPORT's point-band ids a dot each
 * (`scene/viewportPointProxies.ts`) and fed them into the ONE cloud
 * `scene/FrameLoops.tsx` builds from the main camera's `groups.point`. That
 * cloud carries nothing but `kind: "lod-points-cloud"`, and every mask in the
 * paper-space pipeline is keyed on `entityId` / `keyId`
 * (`rendering/viewportRenderPass.ts applySceneVisibilityMask`) or on
 * `entityId` / `keyId` / `spaceId` (`rendering/viewportTraversalCaches.ts`).
 * So the union of every viewport's ids was drawn, unmasked, in EVERY viewport
 * pass and on the bare sheet:
 *
 *   1. a dot viewport A asked for painted inside viewport B, which never asked
 *      for that id (and, at B's own smaller scale, classifies it `"skip"`);
 *   2. a dot whose layer is VP-frozen in the viewport it is drawn in stayed
 *      visible — the mask cannot reach an untagged cloud;
 *   3. the cloud was absent from the model-space `preHidable` set, so
 *      model-space dots painted on the bare paper outside every VP scissor.
 *
 * # The stamp
 *
 * A proxy cloud is built per (requesting-viewport set, layer) group, so every
 * dot in one cloud shares BOTH attributions and one object-level mask decision
 * is exact for all of them. The stamp reuses the vocabulary the masks already
 * speak — `entityLayer` (+ the layer-chain predicate), `spaceId` — and adds
 * only the membership list, which has no equivalent because nothing else in
 * the scene is owned by a named viewport.
 *
 * The MAIN camera's own cloud carries NO stamp (`readViewportPointProxy`
 * returns `null` for it) and therefore keeps its pre-round-11 behaviour
 * exactly: drawn in every pass, never pre-hidden.
 */

/** What one attributed proxy cloud is scoped to. */
export interface ViewportPointProxyStamp {
  /**
   * The ids of the viewports that REQUESTED the dots in this cloud, i.e. the
   * only passes it may draw in. Never empty — a group with no requester is
   * never built.
   */
  readonly viewportIds: readonly string[];
  /** The entity layer every dot in this cloud sits on (one layer per cloud). */
  readonly layer: string;
}

/**
 * The userData a proxy cloud carries, merged over `buildPointsCloud`'s own
 * `kind: "lod-points-cloud"` (which is what keeps the cloud in the step-5
 * frame-only hide set).
 *
 * `spaceId: "model"` is a fact, not a hint: `scene/viewportPointProxies.ts`
 * drops any proxy that does not resolve to model space, because a viewport is
 * a window onto model space only. It is the key
 * `rendering/viewportTraversalCaches.ts` reads to put the cloud in the
 * model-space `preHidable` set (leak 3).
 */
export function viewportPointProxyUserData(
  stamp: ViewportPointProxyStamp,
): Record<string, unknown> {
  return {
    viewportPointProxy: true,
    viewportIds: [...stamp.viewportIds],
    entityLayer: stamp.layer,
    spaceId: "model",
  };
}

/**
 * Read the stamp back off an object's userData, or `null` when the object is
 * not an attributed proxy cloud (the main camera's cloud, a bucket, a direct
 * mesh, a foreign object).
 */
export function readViewportPointProxy(
  userData: Readonly<Record<string, unknown>>,
): ViewportPointProxyStamp | null {
  if (userData["viewportPointProxy"] !== true) return null;
  const ids = userData["viewportIds"];
  const layer = userData["entityLayer"];
  if (!Array.isArray(ids) || typeof layer !== "string") return null;
  return { viewportIds: ids as readonly string[], layer };
}

/**
 * True when `viewportId` is one of the viewports that requested this cloud.
 *
 * An UNNAMED pass (`viewportId === undefined`) never draws a proxy cloud: the
 * cloud exists only because some viewport asked for it, so a caller that
 * cannot say which viewport it is rendering must not paint it.
 */
export function viewportPointProxyDrawsIn(
  stamp: ViewportPointProxyStamp,
  viewportId: string | undefined,
): boolean {
  return viewportId !== undefined && stamp.viewportIds.includes(viewportId);
}
